import { svgPathBbox } from 'svg-path-bbox';
import svgPath from 'svgpath';
import {
    createCanvas,
    EmptyPointImage,
    fetchVectorText,
    formatSvgPathDecimals,
    generateGradient,
    generateGradientToCtx,
    getGradientId,
    getVectorTextFromCache,
    parseColorToRgba,
    parseStrokeDasharray,
} from '@/utils/file-kit';
import {
    BrushPointType,
    BrushType,
    type IBrushAttribute,
    type IBrushLayerProps,
    type ILayerProps,
    type ILinearGradient,
} from '@/version-types';
import {
    BRUSH_POINT_SIZE,
    createBrushImage,
    createCtxTarget,
    drawBrushStroke,
    exportBrushPath,
    generateLinePath,
    getArrowPointImage,
    getPathOffset,
    isArrowSvg,
    isCodeSvg,
    isCurveSvg,
    isPolygonSvg,
    isWavySvg,
    matchRepeatPath,
} from './layer';

interface IPointImage {
    width: number;
    height: number;
    element: {
        tag: string;
        attrs: Record<string, unknown>;
    };
}

function isGradient(color: IBrushAttribute['color']) {
    return typeof color !== 'string';
}

function getCanvasStyle(ctx: CanvasRenderingContext2D, width: number, height: number, color: IBrushAttribute['color']) {
    return isGradient(color) ? generateGradientToCtx(ctx, width, height, color as ILinearGradient) : (color as string);
}

function getSvgPaint(color: IBrushAttribute['color'], width: number, height: number, defs: string[]) {
    if (!isGradient(color)) return color as string;

    const id = `ATTR_${getGradientId()}`;
    defs.push(generateGradient(id, width, height, color as ILinearGradient));
    return `url(#${id})`;
}

/**
 * 只返回当前笔刷渲染实际依赖的远程素材，本地路径存在时不等待上传后的 URL
 * @param data - 当前笔刷属性
 * @returns 去重后的有效素材 URL
 */
export function getBrushResourceUrls(data: IBrushLayerProps): string[] {
    const urls = isArrowSvg(data.brushType)
        ? [data.startPoint?.url, data.endPoint?.url]
        : !isCodeSvg(data.brushType) && !data.__drawPath
          ? [data.url]
          : [];
    return [...new Set(urls.filter((url): url is string => !!url))];
}

async function preloadBrushSvgAsync(data: IBrushLayerProps) {
    await Promise.all(getBrushResourceUrls(data).map((url) => fetchVectorText(url)));
}

function getBrushPath(data: IBrushLayerProps) {
    const { url, __drawPath, brushType, naturalWidth } = data;

    if (__drawPath) return __drawPath;
    if (url) {
        const text = getVectorTextFromCache(url);
        return text ? exportBrushPath(text) : '';
    }
    if (brushType === BrushType.Line) {
        return generateLinePath({ x: 0, y: 0 }, { x: naturalWidth, y: 0 });
    }
    return '';
}

function getLocalPreviewPath(layer: ILayerProps, pathD: string) {
    const { __isDrawPreview } = layer.data as IBrushLayerProps;
    if (!__isDrawPreview) return pathD;

    const [minX, minY, maxX, maxY] = svgPathBbox(pathD);
    const isInLayer = minX >= 0 && minY >= 0 && maxX <= layer.width && maxY <= layer.height;
    if (isInLayer) return pathD;

    return formatSvgPathDecimals(svgPath(pathD).translate(-layer.left, -layer.top).toString());
}

function getArrowPoint(pointUrl: string, pointInfo: IBrushLayerProps['startPoint'], fill: IBrushAttribute) {
    const text = getVectorTextFromCache(pointUrl);
    return text ? getArrowPointImage(text, pointInfo, fill) : (EmptyPointImage as IPointImage);
}

function pointToSvg(point: IPointImage, transform: string) {
    const { tag, attrs } = point.element;
    if (!tag) return '';

    if (tag === 'circle') {
        return `<circle cx="${attrs.cx || 0}" cy="${attrs.cy || 0}" r="${attrs.r || 0}" transform="${transform}" />`;
    }

    const fill = attrs.fill ? ` fill="${attrs.fill}"` : '';
    return `<path d="${attrs.d || ''}"${fill} stroke-width="${attrs.strokeWidth || 0}" stroke-linecap="${
        attrs.strokeLinecap || ''
    }" transform="${transform}" />`;
}

function drawPointImage(ctx: CanvasRenderingContext2D, pointImg: IPointImage) {
    const { tag, attrs } = pointImg.element;
    if (!tag) return;

    if (tag === 'circle') {
        ctx.beginPath();
        ctx.arc(Number(attrs.cx || 0), Number(attrs.cy || 0), Number(attrs.r || 0), 0, Math.PI * 2);
        ctx.fill();
        return;
    }

    const path = new Path2D(String(attrs.d || ''));
    const fill = attrs.fill;
    const shouldStroke = fill === 'none' || Number(attrs.strokeWidth || 0) > 0;

    if (fill !== 'none') {
        ctx.fill(path);
    }
    if (shouldStroke) {
        ctx.lineWidth = Number(attrs.strokeWidth || 1);
        ctx.lineCap = (attrs.strokeLinecap as CanvasLineCap) || 'butt';
        ctx.stroke(path);
    }
}

function drawPoint(
    ctx: CanvasRenderingContext2D,
    pointImg: IPointImage,
    transform: {
        x: number;
        y: number;
        flipX?: boolean;
    },
) {
    if (!pointImg.element.tag) return;

    ctx.save();
    ctx.translate(transform.x, transform.y);
    if (transform.flipX) ctx.scale(-1, 1);
    ctx.setLineDash([]);
    drawPointImage(ctx, pointImg);
    ctx.restore();
}

function drawRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) {
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

function drawCodeBrush(ctx: CanvasRenderingContext2D, layer: ILayerProps) {
    const { width, height } = layer;
    const { brushType, radius, fills, stroke } = layer.data as IBrushLayerProps;
    const fill = fills[0];
    const strokeWidth = stroke.width || 0;
    const drawWidth = Math.max(width - strokeWidth, 1);
    const drawHeight = Math.max(height - strokeWidth, 1);

    ctx.save();
    ctx.fillStyle = getCanvasStyle(ctx, width, height, fill.color);
    ctx.strokeStyle = getCanvasStyle(ctx, width, height, stroke.color);
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = stroke.linecap as CanvasLineCap;
    if (stroke.dasharray) ctx.setLineDash(parseStrokeDasharray(stroke.dasharray, strokeWidth, false) as number[]);

    if (brushType === BrushType.Rect) {
        const x = strokeWidth / 2;
        const y = strokeWidth / 2;
        if (radius) {
            drawRoundRect(ctx, x, y, drawWidth, drawHeight, radius);
            ctx.fill();
            if (strokeWidth) ctx.stroke();
        } else {
            ctx.fillRect(x, y, drawWidth, drawHeight);
            if (strokeWidth) ctx.strokeRect(x, y, drawWidth, drawHeight);
        }
    } else {
        ctx.beginPath();
        ctx.ellipse(width / 2, height / 2, drawWidth / 2, drawHeight / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        if (strokeWidth) ctx.stroke();
    }
    ctx.restore();
}

function drawArrowBrush(ctx: CanvasRenderingContext2D, layer: ILayerProps) {
    const { width } = layer;
    const { fills, startPoint, endPoint } = layer.data as IBrushLayerProps;
    const fill = fills[0];
    const startPointImg = getArrowPoint(startPoint.url, startPoint, fill);
    const endPointImg = getArrowPoint(endPoint.url, endPoint, fill);

    const startPointFixOffset = startPoint.type === BrushPointType.StraightArrow ? (20 / 100) * fill.width : 0;
    const endPointFixOffset = endPoint.type === BrushPointType.StraightArrow ? (20 / 100) * fill.width : 0;

    ctx.save();
    const color = getCanvasStyle(ctx, width, fill.width, fill.color);
    if (typeof fill.color === 'string') {
        const rgba = parseColorToRgba(fill.color);
        ctx.globalAlpha = rgba.a;
        const rgb = `rgb(${rgba.r},${rgba.g},${rgba.b})`;
        ctx.fillStyle = rgb;
        ctx.strokeStyle = rgb;
    } else {
        ctx.fillStyle = color;
        ctx.strokeStyle = color;
    }
    ctx.translate(0, (layer.height - fill.width) / 2);
    ctx.lineWidth = fill.width;
    ctx.lineCap = fill.linecap as CanvasLineCap;
    if (fill.dasharray) ctx.setLineDash(parseStrokeDasharray(fill.dasharray, fill.width, false) as number[]);

    drawPoint(ctx, startPointImg, {
        x: startPointFixOffset,
        y: -(startPointImg.height - fill.width) / 2,
    });

    ctx.beginPath();
    ctx.moveTo((startPoint.offset / BRUSH_POINT_SIZE) * fill.width + startPointFixOffset, fill.width / 2);
    ctx.lineTo(width - (endPoint.offset / BRUSH_POINT_SIZE) * fill.width - endPointFixOffset, fill.width / 2);
    ctx.stroke();

    drawPoint(ctx, endPointImg, {
        x: width - endPointFixOffset,
        y: -(endPointImg.height - fill.width) / 2,
        flipX: true,
    });
    ctx.restore();
}

function drawWavyBrush(ctx: CanvasRenderingContext2D, layer: ILayerProps, pathD: string) {
    const { width } = layer;
    const { fills, naturalWidth } = layer.data as IBrushLayerProps;
    const fill = fills[0];
    const repeatD = matchRepeatPath(pathD);
    const repeatPathD = Array.from({ length: Math.ceil(width / naturalWidth) || 0 }, () => repeatD).join('');
    const path = new Path2D(`${pathD}${repeatPathD}`);

    ctx.save();
    ctx.translate(0, fill.width / 2 || 0);
    createCtxTarget(ctx, path, fill, layer.width, layer.height, 'source-over');
    ctx.restore();
}

function fillPath(ctx: CanvasRenderingContext2D, path: Path2D, attr: IBrushAttribute, width: number, height: number) {
    ctx.save();
    ctx.fillStyle = getCanvasStyle(ctx, width, height, attr.color);
    ctx.fill(path);
    ctx.restore();
}

function drawPolygonBrush(ctx: CanvasRenderingContext2D, layer: ILayerProps, pathD: string) {
    const { width, height } = layer;
    const { fills, stroke, naturalWidth, naturalHeight } = layer.data as IBrushLayerProps;
    const offset = stroke.width;
    const targetPathD = formatSvgPathDecimals(
        svgPath(pathD)
            .scale((width - offset) / naturalWidth, (height - offset) / naturalHeight)
            .translate(offset / 2, offset / 2)
            .toString(),
    );
    const path = new Path2D(targetPathD);

    fillPath(ctx, path, fills[0], width, height);
    if (stroke.width) createCtxTarget(ctx, path, stroke, width, height, 'source-over');
}

function drawPathBrush(ctx: CanvasRenderingContext2D, layer: ILayerProps, pathD: string) {
    const { width, height } = layer;
    const { brushType, __isDrawPreview, fills, stroke, naturalWidth, naturalHeight } = layer.data as IBrushLayerProps;

    if (isWavySvg(brushType)) {
        drawWavyBrush(ctx, layer, pathD);
        return;
    }

    if (isPolygonSvg(brushType)) {
        drawPolygonBrush(ctx, layer, pathD);
        return;
    }

    const targets: Array<{ pathD: string; attr: IBrushAttribute }> = [];
    let targetPathD = pathD;
    fills.forEach((fill, index) => {
        if (index === 0) {
            if (!__isDrawPreview) {
                const res = getPathOffset(fills, stroke);
                const newWidth = width - res.offsetWidth;
                const newHeight = height - res.offsetHeight;
                targetPathD = formatSvgPathDecimals(
                    svgPath(targetPathD)
                        .scale(newWidth / naturalWidth, newHeight / naturalHeight)
                        .translate(res.translate, res.translate)
                        .toString(),
                );
            }
            targets.push({ pathD: targetPathD, attr: fill });
            return;
        }

        targetPathD = formatSvgPathDecimals(svgPath(targetPathD).translate(fill.offsetX, fill.offsetY).toString());
        targets.unshift({ pathD: targetPathD, attr: fill });
    });

    targets.forEach(({ pathD: target, attr }) => {
        createCtxTarget(ctx, new Path2D(target), attr, width, height, 'source-over');
    });
}

/**
 * 在瞬态预览的上下文中同步绘制笔刷，不分配整层位图
 *
 * 复用正式渲染的形状、端点和填充规则；调用方负责素材预加载、坐标变换和清屏。
 * 描边镂空直接作用于仅包含当前笔迹的预览，不能用于已含其他图层的上下文。
 *
 * @param ctx - 仅供当前笔迹使用的 Canvas 上下文，函数会绘制笔刷内容
 * @param layer - 与原绘制预览相同的图层数据，不会修改
 * @returns 是否具备所需路径和素材并完成绘制
 */
export function drawBrushPreviewContent(ctx: CanvasRenderingContext2D, layer: ILayerProps) {
    const data = layer.data as IBrushLayerProps;
    const { brushType, fills, stroke, naturalWidth, naturalHeight } = data;
    if (!fills.length) return false;
    const urls = getBrushResourceUrls(data);
    if (urls.some((url) => url && !getVectorTextFromCache(url))) return false;

    if (isCodeSvg(brushType)) {
        drawCodeBrush(ctx, layer);
        return true;
    }
    if (isArrowSvg(brushType)) {
        drawArrowBrush(ctx, layer);
        return true;
    }
    let pathD = getBrushPath(data);
    if (!pathD) return false;
    pathD = getLocalPreviewPath(layer, pathD);
    if (isCurveSvg(brushType) && stroke?.width) {
        let strokePath = pathD;
        if (!data.__isDrawPreview) {
            const offset = getPathOffset(fills, stroke);
            strokePath = formatSvgPathDecimals(
                svgPath(pathD)
                    .scale(
                        (layer.width - offset.offsetWidth) / naturalWidth,
                        (layer.height - offset.offsetHeight) / naturalHeight,
                    )
                    .translate(offset.translate, offset.translate)
                    .toString(),
            );
        }
        const path = new Path2D(strokePath);
        createCtxTarget(
            ctx,
            path,
            { ...stroke, width: fills[0].width + stroke.width * 2 },
            naturalWidth,
            naturalHeight,
            'source-over',
        );
        createCtxTarget(
            ctx,
            path,
            { ...fills[0], color: '#000' },
            naturalWidth,
            naturalHeight,
            'destination-out',
            true,
        );
    }
    drawPathBrush(ctx, layer, pathD);
    return true;
}

function createCodeBrushSvgRaw(layer: ILayerProps) {
    const { id, width, height } = layer;
    const { brushType, radius, fills, stroke } = layer.data as IBrushLayerProps;
    const defs: string[] = [];
    const fill = getSvgPaint(fills[0].color, width, height, defs);
    const strokeColor = getSvgPaint(stroke.color, width, height, defs);
    const strokeWidth = stroke.width || 0;
    const commonAttrs = `fill="${fill}" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-dasharray="${
        parseStrokeDasharray(stroke.dasharray || '', strokeWidth) || ''
    }" stroke-linecap="${stroke.linecap || ''}"`;
    const shape =
        brushType === BrushType.Rect
            ? `<rect x="${strokeWidth / 2}" y="${strokeWidth / 2}" rx="${radius}" ry="${radius}" width="${Math.max(
                  width - strokeWidth,
                  1,
              )}" height="${Math.max(height - strokeWidth, 1)}" ${commonAttrs} />`
            : `<ellipse cx="${width / 2}" cy="${height / 2}" rx="${Math.max(
                  width / 2 - strokeWidth / 2,
                  1,
              )}" ry="${Math.max(height / 2 - strokeWidth / 2, 1)}" ${commonAttrs} />`;

    return `<svg data-draw-id="svg-${id}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" version="1.1" style="display:block;overflow:hidden">${
        defs.length ? `<defs>${defs.join('')}</defs>` : ''
    }${shape}</svg>`;
}

function createArrowBrushSvgRaw(layer: ILayerProps) {
    const { id, width } = layer;
    const { fills, startPoint, endPoint } = layer.data as IBrushLayerProps;
    const fill = fills[0];
    const startPointImg = getArrowPoint(startPoint.url, startPoint, fill);
    const endPointImg = getArrowPoint(endPoint.url, endPoint, fill);
    const startPointFixOffset = startPoint.type === BrushPointType.StraightArrow ? (20 / 100) * fill.width : 0;
    const endPointFixOffset = endPoint.type === BrushPointType.StraightArrow ? (20 / 100) * fill.width : 0;
    const defs: string[] = [];
    const color = getSvgPaint(fill.color, width, fill.width, defs);

    return `<svg data-draw-id="svg-${id}" viewBox="0 0 ${width} ${
        fill.width
    }" xmlns="http://www.w3.org/2000/svg" version="1.1" style="display:block;overflow:visible" stroke="${color}" fill="${color}">${
        defs.length ? `<defs>${defs.join('')}</defs>` : ''
    }${pointToSvg(startPointImg, `translate(${startPointFixOffset},-${(startPointImg.height - fill.width) / 2})`)}
    <line x1="${
        (startPoint.offset / BRUSH_POINT_SIZE) * fill.width + startPointFixOffset
    }" y1="${fill.width / 2}" x2="${
        width - (endPoint.offset / BRUSH_POINT_SIZE) * fill.width - endPointFixOffset
    }" y2="${fill.width / 2}" stroke-width="${fill.width}" stroke-dasharray="${
        parseStrokeDasharray(fill.dasharray || '', fill.width) || ''
    }" stroke-linecap="${fill.linecap}" />
    ${pointToSvg(
        endPointImg,
        `translate(${width - endPointFixOffset},-${(endPointImg.height - fill.width) / 2}) scale(-1,1)`,
    )}</svg>`;
}

function createWavyBrushSvgRaw(layer: ILayerProps, pathD: string) {
    const { id, width, height } = layer;
    const { fills, naturalWidth } = layer.data as IBrushLayerProps;
    const fill = fills[0];
    const repeatD = matchRepeatPath(pathD);
    const repeatPathD = Array.from({ length: Math.ceil(width / naturalWidth) || 0 }, () => repeatD).join('');
    const defs: string[] = [];
    const stroke = getSvgPaint(fill.color, width, height, defs);

    return `<svg data-draw-id="svg-${id}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" version="1.1" style="display:block;overflow:hidden">${
        defs.length ? `<defs>${defs.join('')}</defs>` : ''
    }<path d="${pathD}${repeatPathD}" fill="none" stroke="${stroke}" stroke-width="${
        fill.width
    }" stroke-dasharray="${parseStrokeDasharray(fill.dasharray || '', fill.width) || ''}" stroke-linecap="${
        fill.linecap || ''
    }" style="transform:translateY(${fill.width / 2 || 0}px)" /></svg>`;
}

function createBrushSvgRaw(layer: ILayerProps) {
    const data = layer.data as IBrushLayerProps;
    const { brushType } = data;

    if (isCodeSvg(brushType)) return createCodeBrushSvgRaw(layer);
    if (isArrowSvg(brushType)) return createArrowBrushSvgRaw(layer);

    const pathD = getBrushPath(data);
    if (!pathD) return '';
    if (isWavySvg(brushType)) return createWavyBrushSvgRaw(layer, pathD);

    return createBrushImage(pathD, {
        brushType,
        fills: data.fills,
        stroke: data.stroke,
        naturalWidth: data.naturalWidth,
        naturalHeight: data.naturalHeight,
        __isDrawPreview: data.__isDrawPreview,
        width: layer.width,
        height: layer.height,
    }).html;
}

export class BrushCreator {
    static async getBrushSvgRawAsync(layer: ILayerProps, _identity: string) {
        let loadSvgError = false;
        try {
            await preloadBrushSvgAsync(layer.data as IBrushLayerProps);
        } catch (error) {
            console.log('load svg error', error);
            loadSvgError = true;
        }

        if (loadSvgError) return '';
        return createBrushSvgRaw(layer);
    }
    static async create(layer: ILayerProps, _identity: string, retinaScaling = 1) {
        const { width, height } = layer;
        const data = layer.data as IBrushLayerProps;
        const { brushType } = data;

        let loadSvgError = false;
        try {
            await preloadBrushSvgAsync(data);
        } catch (error) {
            console.log('load svg error', error);
            loadSvgError = true;
        }

        if (loadSvgError) return createCanvas(1, 1).canvas;

        const { canvas, ctx } = createCanvas(
            Math.max(1, Math.floor(width * retinaScaling)),
            Math.max(1, Math.floor(height * retinaScaling)),
        );
        ctx.scale(retinaScaling, retinaScaling);

        if (isCodeSvg(brushType)) {
            drawCodeBrush(ctx, layer);
            return canvas;
        }

        if (isArrowSvg(brushType)) {
            drawArrowBrush(ctx, layer);
            return canvas;
        }

        let pathD = getBrushPath(data);
        if (!pathD) return createCanvas(1, 1).canvas;
        pathD = getLocalPreviewPath(layer, pathD);

        if (isCurveSvg(brushType) && data.stroke?.width) {
            const strokeScale = Math.min(1, retinaScaling);
            const { canvas: strokeCanvas } = createCanvas(
                Math.max(1, Math.floor(width * strokeScale)),
                Math.max(1, Math.floor(height * strokeScale)),
            );
            await drawBrushStroke(
                data.__isDrawPreview || false,
                strokeCanvas,
                pathD,
                {
                    fills: data.fills,
                    stroke: data.stroke,
                    width,
                    height,
                    naturalWidth: data.naturalWidth,
                    naturalHeight: data.naturalHeight,
                },
                strokeScale,
            );
            ctx.drawImage(strokeCanvas, 0, 0, width, height);
        }

        drawPathBrush(ctx, layer, pathD);

        return canvas;
    }
}
